/* eslint-disable max-lines */
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import {
  CustomerBoIAM,
  useGetCustomerBoIAM
} from '@utils/api/back-office/customer-bo/use-get-customer-bo-iam.tsx';
import { Params, useParams } from 'react-router-dom';
import Input from '@components/atoms/ui-kit/input.tsx';
import {
  Control,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister
} from 'react-hook-form';
import {
  CreateGroupBoPayload,
  useCreateCustomerIamGroup
} from '@utils/api/back-office/customer-bo/use-create-group.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import Checkbox from '@components/atoms/ui-kit/checkbox.tsx';

type Props = {
  customer?: number;
  handleAddMemberToTeam(user: CustomerBoIAM): void;
  handleRemoveMemberFromTeam(id: string): void;
  teamUsersData: CustomerBoIAM[];
  register: UseFormRegister<CreateGroupBoPayload>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<CreateGroupBoPayload, any>;
  errors: FieldErrors<CreateGroupBoPayload>;
  setIsCreateModalOpen: Dispatch<SetStateAction<boolean>>;
  isCreateModalOpen: boolean;
  handleSubmit: UseFormHandleSubmit<CreateGroupBoPayload, CreateGroupBoPayload>;
};

export default function CreateIAmGroup({
  teamUsersData,
  handleRemoveMemberFromTeam,
  handleAddMemberToTeam,
  register,
  errors,
  isCreateModalOpen,
  setIsCreateModalOpen,
  handleSubmit
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const { customerId } = useParams<Params>();

  const { data: users } = useGetCustomerBoIAM(Number(customerId));
  const { mutate: createGroup, isLoading } = useCreateCustomerIamGroup(
    Number(customerId)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [query, setQuery] = useState('');

  const onSubmit = (data: CreateGroupBoPayload) => {
    const payload: CreateGroupBoPayload = {
      name: data.name,
      member_ids: teamUsersData.map(user => user.id),
      description: data.description
    };

    setIsSubmitting(true);

    createGroup(payload, {
      onSuccess: () => {
        toast.success(t('group-created-successfully'));
        setIsCreateModalOpen(false);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        let messages: string[] = [];

        if (Array.isArray(error?.response?.data?.detail)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          messages = error.response.data.detail.map((err: any) => {
            const field = err.loc?.[1] ?? 'field';
            return `${field}: ${err.msg}`;
          });
        } else if (error?.response?.data?.error_description) {
          messages = [error.response.data.error_description];
        } else if (error?.response?.data?.message) {
          messages = [error.response.data.message];
        } else {
          messages = [t('failed-to-create-group')];
        }

        toast.error(messages.join('\n'));
      },
      onSettled: () => {
        setIsSubmitting(false);
      }
    });
  };

  const styles = useMemo(
    () =>
      StyleSheet.create({
        mainContainerColor: {
          backgroundColor: getColour('appSoft')
        },
        mainText: {
          color: getColour('normalText')
        },
        border: {
          borderColor: getColour('hairline')
        }
      }),
    [getColour]
  );

  const FORM_ID = 'create-iam-group-form';

  const allUsers = users?.result ?? [];

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allUsers;
    return allUsers.filter(u => {
      const full = `${u.first_name ?? ''} ${u.last_name ?? ''}`.toLowerCase();
      const email = (u.email ?? '').toLowerCase();
      return full.includes(q) || email.includes(q);
    });
  }, [allUsers, query]);

  const selectedIds = useMemo(
    () => new Set(teamUsersData.map(u => u.id)),
    [teamUsersData]
  );

  const selectedCount = selectedIds.size;

  const toggleUser = (checked: boolean, user: CustomerBoIAM) => {
    if (checked) handleAddMemberToTeam(user);
    else handleRemoveMemberFromTeam(user.id.toString());
  };

  const selectAllFiltered = () => {
    filteredUsers.forEach(u => {
      if (!selectedIds.has(u.id)) handleAddMemberToTeam(u);
    });
  };

  const clearAllFiltered = () => {
    filteredUsers.forEach(u => {
      if (selectedIds.has(u.id)) handleRemoveMemberFromTeam(u.id.toString());
    });
  };

  return (
    <Modal
      isOpen={isCreateModalOpen}
      onClose={() => !isLoading && setIsCreateModalOpen(false)}
      size="large"
      title={t('create-group')}
      footerActions={[
        <Button
          key="cancel"
          onClick={() => setIsCreateModalOpen(false)}
          label={t('cancel')}
          size="small"
          variant="secondary"
          disabled={isSubmitting}
        />,
        <Button
          label={isSubmitting ? t('creating') : t('create')}
          key="create"
          type="submit"
          form={FORM_ID}
          variant="primary"
          size="small"
          disabled={isSubmitting}
        />
      ]}
    >
      <div className="text-left">
        <form
          id={FORM_ID}
          onSubmit={handleSubmit(onSubmit)}
          className={cn(
            'flex flex-col text-left',
            'max-h-[70vh] sm:max-h-[65vh]',
            css(styles.mainContainerColor, styles.mainText)
          )}
        >
          <button type="submit" className="hidden" aria-hidden="true" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              id="name"
              label={t('name')}
              type="text"
              autoComplete="name"
              {...register('name')}
              error={errors?.name?.message}
            />
            <Input
              id="description"
              label={t('description')}
              type="text"
              autoComplete="description"
              {...register('description')}
              error={errors?.description?.message}
            />
          </div>

          <fieldset className="mt-6 flex-1 min-h-0">
            <legend className="block text-sm font-medium mb-2">
              {t('members')}
            </legend>

            <div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="w-full sm:w-auto flex-1">
                  <Input
                    id="member-search"
                    placeholder={t('search-by-name-or-email')}
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    aria-label={t('search')}
                  />
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="xs"
                    variant="light"
                    onClick={selectAllFiltered}
                    label={t('select-all')}
                  />
                  <Button
                    size="xs"
                    variant="smoke"
                    onClick={clearAllFiltered}
                    label={t('clear')}
                  />
                  <span className="text-xs font-medium opacity-80 whitespace-nowrap">
                    {selectedCount} {t('selected')}
                  </span>
                </div>
              </div>
            </div>

            <div
              className="min-h-0 overflow-auto pr-1 rounded-2xl"
              style={{
                maxHeight: '42vh'
              }}
            >
              {filteredUsers.length === 0 ? (
                <div className="py-12 px-3 text-sm text-center opacity-60">
                  {query
                    ? t('no-users-match-your-search')
                    : t('no-users-available')}
                </div>
              ) : (
                <div className="grid gap-2.5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 mt-4">
                  {filteredUsers.map((user: CustomerBoIAM) => {
                    const checked = selectedIds.has(user.id);
                    return (
                      <div
                        key={user.id}
                        className="p-2 rounded-2xl border border-hairline transition-all duration-200 shadow-sm hover:shadow"
                        style={{
                          backgroundColor: getColour('thirdLayer')
                        }}
                      >
                        <Checkbox
                          id={`member_${user.id}`}
                          label={
                            <span className="block truncate">
                              {user.first_name} {user.last_name}
                              {user.email ? (
                                <span className="block text-xs opacity-70 truncate">
                                  {user.email}
                                </span>
                              ) : null}
                            </span>
                          }
                          checked={checked}
                          onChange={e => toggleUser(e.target.checked, user)}
                          variant="primary"
                          wrapperClass="flex items-center gap-3"
                          labelClass="truncate"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </fieldset>
        </form>
      </div>
    </Modal>
  );
}
